/**
 * @file apps/web/src/components/GapCard.tsx
 * @description Card component for displaying a single capability gap detected
 * during test-script generation — a requirement with no full native NetSuite
 * support, along with the suggested FDD (Functional Design Document) language
 * to cover the customization.
 */

import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { Badge } from './Badge.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UatGap {
  id:                string;
  subcycle_id:       string;
  requirement_text:  string;
  rule_id:           string;
  fdd_suggestion:    string;
  created_at:        string;
  // Joined from the NetSuite rule database by rule_id — null if the rule
  // no longer exists in the rule database.
  native_support:    'partial' | 'none' | null;
}

interface GapCardProps {
  gap:       UatGap;
  className?: string;
}

const FDD_TRUNCATE_LENGTH = 220;

function NativeSupportBadge({ nativeSupport }: { nativeSupport: UatGap['native_support'] }) {
  if (nativeSupport === 'partial') return <Badge variant="medium" label="Partial Support" dot />;
  if (nativeSupport === 'none')    return <Badge variant="fail" label="No Native Support" dot />;
  return <Badge variant="muted" label="Unmatched Rule" dot />;
}

// ---------------------------------------------------------------------------
// GapCard
// ---------------------------------------------------------------------------

export function GapCard({ gap, className = '' }: GapCardProps) {
  const [fddExpanded, setFddExpanded] = useState(false);

  const fddIsLong = gap.fdd_suggestion.length > FDD_TRUNCATE_LENGTH;
  const fddDisplay = fddIsLong && !fddExpanded
    ? gap.fdd_suggestion.slice(0, FDD_TRUNCATE_LENGTH).trimEnd() + '…'
    : gap.fdd_suggestion;

  return (
    <div className={['card p-4 space-y-3', className].join(' ')}>
      {/* Header: rule id badge + native support badge */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-gray text-ink/80">
          {gap.rule_id}
        </span>
        <NativeSupportBadge nativeSupport={gap.native_support} />
      </div>

      {/* Requirement text */}
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="text-muted shrink-0 mt-0.5" />
        <p className="text-sm text-ink leading-relaxed">{gap.requirement_text}</p>
      </div>

      {/* FDD suggestion */}
      <div className="bg-sky/40 rounded p-3">
        <p className="text-[11px] font-mono font-semibold text-blue uppercase tracking-wider mb-1">
          FDD Suggestion
        </p>
        <p className="text-sm text-ink/80 leading-relaxed whitespace-pre-wrap">{fddDisplay}</p>
        {fddIsLong && (
          <button
            onClick={() => setFddExpanded((v) => !v)}
            className="flex items-center gap-1 text-xs text-blue hover:underline font-mono mt-2"
          >
            {fddExpanded ? <>Show less <ChevronUp size={12} /></> : <>Show more <ChevronDown size={12} /></>}
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end">
        <button type="button" className="btn-secondary text-xs py-1 px-2">
          <Plus size={13} /> Add to FDD
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GapList — renders a list of GapCards
// ---------------------------------------------------------------------------

interface GapListProps {
  gaps:          UatGap[];
  loading?:      boolean;
  emptyMessage?: string;
}

export function GapList({ gaps, loading, emptyMessage }: GapListProps) {
  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card h-24 bg-gray/60" />
        ))}
      </div>
    );
  }

  if (gaps.length === 0) {
    return (
      <div className="empty-state">
        <AlertTriangle size={32} className="text-muted/40 mb-3" />
        <p className="text-sm text-muted">{emptyMessage ?? 'No gaps detected.'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {gaps.map((g) => <GapCard key={g.id} gap={g} />)}
    </div>
  );
}
